import { useState, useCallback, useEffect, useRef } from 'react'
import timeEntryService from '@api/v1/timeEntryService'

/**
 * Interactive billing entry card — user reviews, edits, and approves
 * an AI-proposed time or expense entry before submitting to Centerbase.
 */
export default function BillingEntryCard({ proposal, onComplete }) {
  const [status, setStatus] = useState('pending') // pending | editing | submitting | approved | failed
  const [error, setError] = useState(null)
  const [billingCodes, setBillingCodes] = useState([])
  const [fields, setFields] = useState({
    entryType: proposal.entry_type || 'time',
    matterName: proposal.matter_name || '',
    matterId: proposal.matter_id || '',
    hours: proposal.hours || '',
    rate: proposal.rate || '',
    amount: proposal.amount || 0,
    date: proposal.date || new Date().toISOString().split('T')[0],
    description: proposal.description || '',
    billingCode: proposal.billing_code || '',
    billingCodeId: proposal.billing_code_id || '',
    isBillable: proposal.is_billable !== false,
    attorneyId: proposal.attorney_id || '',
    attorneyName: proposal.attorney_name || '',
  })

  // Receipt upload state
  const [receiptStatus, setReceiptStatus] = useState('idle')
  const [receiptPreview, setReceiptPreview] = useState(null)
  const [receiptError, setReceiptError] = useState(null)
  const [dragging, setDragging] = useState(false)
  const fileInputRef = useRef(null)

  const handleReceiptFile = useCallback(async (file) => {
    if (!file.type.startsWith('image/')) {
      setReceiptError('Please upload an image (JPEG, PNG, WebP)')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      setReceiptError('Image too large (max 5MB)')
      return
    }
    setReceiptError(null)
    setReceiptStatus('uploading')

    const reader = new FileReader()
    reader.onload = (e) => setReceiptPreview(e.target.result)
    reader.readAsDataURL(file)

    try {
      const result = await timeEntryService.extractReceipt(file)
      setFields((prev) => ({
        ...prev,
        amount: result.amount ?? prev.amount,
        date: result.date ?? prev.date,
        description: result.description
          ? (result.vendor ? `${result.vendor} — ${result.description}` : result.description)
          : prev.description,
      }))
      setReceiptStatus('done')
    } catch (err) {
      setReceiptError(err.message || 'Failed to analyze receipt')
      setReceiptStatus('error')
    }
  }, [])

  // Load billing codes — respect AI-selected code + prepopulate rate from code
  useEffect(() => {
    timeEntryService.getBillingCodes(fields.entryType).then((codes) => {
      setBillingCodes(codes || [])
      if (!codes?.length) return

      // If AI already selected a billing_code_id, verify it exists
      if (fields.billingCodeId) {
        const existing = codes.find((c) => c.id === fields.billingCodeId)
        if (existing) {
          // Prepopulate rate from code if not already set
          if (!fields.rate && existing.rate && fields.entryType === 'time') {
            setFields((prev) => ({ ...prev, rate: prev.rate || existing.rate }))
          }
          return // AI already picked a valid code
        }
      }

      // AI didn't pick, or code not found — match by name or default to first
      const match = fields.billingCode
        ? codes.find((c) => c.name.toLowerCase() === fields.billingCode.toLowerCase())
        : null
      const selected = match || codes[0]
      setFields((prev) => ({
        ...prev,
        billingCodeId: prev.billingCodeId || selected.id,
        billingCode: prev.billingCode || selected.name,
        // Prepopulate rate from matched code if not set
        rate: prev.rate || (prev.entryType === 'time' && selected.rate ? selected.rate : prev.rate),
      }))
    }).catch(() => {})
  }, [])

  const isTime = fields.entryType === 'time'
  const computedAmount = isTime && fields.hours && fields.rate
    ? Math.round(fields.hours * fields.rate * 100) / 100
    : fields.amount || 0

  const updateField = useCallback((key, value) => {
    setFields((prev) => ({ ...prev, [key]: value }))
  }, [])

  const handleApprove = useCallback(async () => {
    setStatus('submitting')
    setError(null)
    try {
      await timeEntryService.saveTimeEntry({
        entryType: fields.entryType,
        matterId: fields.matterId,
        date: fields.date,
        description: fields.description,
        hours: fields.hours ? Number(fields.hours) : null,
        rate: fields.rate ? Number(fields.rate) : null,
        amount: computedAmount,
        billingCodeId: fields.billingCodeId,
        billingCode: fields.billingCode,
        isBillable: fields.isBillable,
      })
      setStatus('approved')
      onComplete?.()
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to create entry')
      setStatus('failed')
    }
  }, [fields, computedAmount, onComplete])

  // Approved state
  if (status === 'approved') {
    return (
      <div className="rounded-xl border border-green-200 bg-green-50">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-green-200">
          <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
          </svg>
          <span className="text-sm font-medium text-green-800">
            {isTime ? 'Time' : 'Expense'} Entry Saved
          </span>
        </div>
        <div className="px-4 py-3 space-y-1">
          <SummaryRow label="Matter" value={fields.matterName} />
          <SummaryRow label="Date" value={fields.date} />
          {isTime ? (
            <>
              <SummaryRow label="Hours" value={fields.hours} />
              <SummaryRow label="Amount" value={`$${computedAmount.toLocaleString()}`} />
            </>
          ) : (
            <SummaryRow label="Amount" value={`$${computedAmount.toLocaleString()}`} />
          )}
          <SummaryRow label="Description" value={fields.description} />
        </div>
      </div>
    )
  }

  const isEditing = status === 'editing'

  return (
    <div className="rounded-xl border border-cb-accent/20 bg-gradient-to-br from-blue-50/50 to-white shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-cb-accent/10">
        <div className="flex items-center gap-2">
          {isTime ? (
            <svg className="w-4 h-4 text-cb-accent-text" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ) : (
            <svg className="w-4 h-4 text-cb-accent-text" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )}
          <span className="text-sm font-medium text-gray-900">
            {isTime ? 'Time Entry' : 'Expense Entry'}
          </span>
        </div>
        <button
          onClick={() => setStatus(isEditing ? 'pending' : 'editing')}
          className="p-1.5 rounded-lg text-gray-500 hover:text-cb-accent-text hover:bg-cb-accent/10 transition-colors"
        >
          {isEditing ? (
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
            </svg>
          ) : (
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487z" />
            </svg>
          )}
        </button>
      </div>

      {/* Receipt drop zone — expense only */}
      {!isTime && receiptStatus !== 'done' && (
        <div
          className={`mx-4 mt-3 border-2 border-dashed rounded-lg p-4 text-center transition-colors cursor-pointer ${
            dragging ? 'border-cb-accent bg-cb-accent/5' : 'border-gray-200 hover:border-cb-accent/50'
          }`}
          onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files[0]; if (f) handleReceiptFile(f) }}
          onClick={() => fileInputRef.current?.click()}
        >
          <input ref={fileInputRef} type="file" accept="image/*" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleReceiptFile(f) }} />
          {receiptStatus === 'uploading' ? (
            <div className="flex flex-col items-center gap-2 py-1">
              <div className="animate-spin w-5 h-5 border-2 border-cb-accent border-t-transparent rounded-full" />
              <span className="text-xs text-cb-accent-text font-medium">Analyzing receipt...</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1.5 py-1 text-gray-500">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
              </svg>
              <span className="text-xs">Drop receipt image or click to upload</span>
            </div>
          )}
          {receiptError && <p className="text-xs text-red-500 mt-2">{receiptError}</p>}
        </div>
      )}

      {!isTime && receiptStatus === 'done' && (
        <div className="mx-4 mt-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-green-50 border border-green-200">
          {receiptPreview && <img src={receiptPreview} alt="Receipt" className="h-8 w-8 rounded object-cover" />}
          <span className="text-xs text-green-700 font-medium">Receipt analyzed — review fields below</span>
        </div>
      )}

      {/* Fields */}
      <div className="px-4 py-3 space-y-2.5">
        {!fields.matterId && (
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-amber-50 border border-amber-200">
            <svg className="w-3.5 h-3.5 text-amber-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
            <span className="text-xs text-amber-700">Could not match a matter. Please select one manually.</span>
          </div>
        )}
        <FieldRow label="Matter" value={fields.matterName} />

        <FieldRow label="Date" value={fields.date} editable type="date"
          onChange={(v) => updateField('date', v)} />

        {isTime ? (
          <div className="grid grid-cols-3 gap-3">
            <EditableField label="Hours" value={fields.hours} type="number"
              onChange={(v) => updateField('hours', v)} />
            <EditableField label="Rate" value={fields.rate} type="number" prefix="$"
              onChange={(v) => updateField('rate', v)} />
            <div>
              <span className="text-[10px] uppercase tracking-wide text-gray-500 block mb-0.5">Amount</span>
              <span className="text-sm font-semibold text-gray-900">${computedAmount.toLocaleString()}</span>
            </div>
          </div>
        ) : (
          <EditableField label="Amount" value={fields.amount} type="number" prefix="$"
            onChange={(v) => updateField('amount', v)} />
        )}

        {isEditing ? (
          <div>
            <span className="text-[10px] uppercase tracking-wide text-gray-500 block mb-0.5">Description</span>
            <textarea value={fields.description} onChange={(e) => updateField('description', e.target.value)}
              rows={2} className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 focus:ring-2 focus:ring-cb-accent/30 focus:border-cb-accent resize-none" />
          </div>
        ) : (
          <div>
            <span className="text-[10px] uppercase tracking-wide text-gray-500 block mb-0.5">Description</span>
            <p className="text-sm text-gray-700">{fields.description}</p>
          </div>
        )}

        {billingCodes.length > 0 && (
          <div>
            <span className="text-[10px] uppercase tracking-wide text-gray-500 block mb-0.5">Billing Code</span>
            <select value={fields.billingCodeId}
              onChange={(e) => {
                const code = billingCodes.find((c) => c.id === e.target.value)
                setFields((prev) => ({
                  ...prev,
                  billingCodeId: e.target.value,
                  billingCode: code?.name || '',
                  // Update rate from code if it has one and this is a time entry
                  rate: (code?.rate && prev.entryType === 'time') ? code.rate : prev.rate,
                }))
              }}
              className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 bg-white focus:ring-2 focus:ring-cb-accent/30 focus:border-cb-accent"
            >
              <option value="">Select billing code...</option>
              {billingCodes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        )}

        <div className="flex items-center gap-2 pt-0.5">
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            fields.isBillable ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
          }`}>
            {fields.isBillable ? 'Billable' : 'Non-billable'}
          </span>
          <button
            role="switch"
            aria-checked={fields.isBillable}
            aria-label="Billable"
            onClick={() => updateField('isBillable', !fields.isBillable)}
            className="text-xs text-cb-accent-text hover:underline">Toggle</button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mx-4 mb-3 p-2.5 rounded-lg bg-red-50 border border-red-200 flex items-start gap-2">
          <svg className="w-3.5 h-3.5 text-red-500 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
          <div className="text-xs text-red-700">
            {error}
            <button onClick={() => { setStatus('pending'); setError(null) }} className="ml-2 underline font-medium">Retry</button>
          </div>
        </div>
      )}

      {/* Actions */}
      {status !== 'approved' && (
        <div className="flex items-center gap-2 px-4 py-3 border-t border-cb-accent/10 bg-blue-50/30 rounded-b-xl">
          <button onClick={handleApprove} disabled={status === 'submitting'}
            className="flex items-center gap-1.5 px-4 py-2 bg-cb-accent hover:bg-cb-accent/90 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            {status === 'submitting' ? (
              <div className="animate-spin w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
            )}
            {status === 'submitting' ? 'Submitting...' : 'Approve & Create'}
          </button>
          {status !== 'submitting' && (
            <button onClick={() => setStatus('editing')}
              className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
              Edit
            </button>
          )}
        </div>
      )}
    </div>
  )
}

function FieldRow({ label, value, editable, type, onChange }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] uppercase tracking-wide text-gray-500 w-16 shrink-0">{label}</span>
      {editable ? (
        <input type={type || 'text'} value={value} onChange={(e) => onChange(e.target.value)}
          className="flex-1 text-sm border border-gray-200 rounded-lg px-2 py-1 focus:ring-2 focus:ring-cb-accent/30 focus:border-cb-accent" />
      ) : (
        <span className="text-sm text-gray-900 font-medium">{value}</span>
      )}
    </div>
  )
}

function EditableField({ label, value, type, prefix, onChange }) {
  return (
    <div>
      <span className="text-[10px] uppercase tracking-wide text-gray-500 block mb-0.5">{label}</span>
      <div className="flex items-center">
        {prefix && <span className="text-sm text-gray-500 mr-0.5">{prefix}</span>}
        <input type={type || 'text'} value={value} onChange={(e) => onChange(e.target.value)}
          step={type === 'number' ? '0.01' : undefined}
          className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1 focus:ring-2 focus:ring-cb-accent/30 focus:border-cb-accent" />
      </div>
    </div>
  )
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex gap-2 text-xs">
      <span className="text-green-600/70 w-20 shrink-0">{label}</span>
      <span className="text-green-900">{value || '—'}</span>
    </div>
  )
}
